import type {
  PaymentMethodCreateRequest,
  PaymentMethodResponse,
  PaymentMethodUpdateRequest,
  PaymentMethodListParams,
  PaymentMethodListResponse,
  PaymentMethodDeleteResponse,
  CustomerPaymentMethodsListParams,
  CustomerPaymentMethodsListResponse,
} from '../types';
import type { RequestFn } from '../client';

/** Create and manage saved payment methods for customers. */
export class PaymentMethods {
  constructor(private readonly request: RequestFn) {}

  /**
   * Save a new payment method (card, bank account, wallet, etc.).
   *
   * @param params - Payment method data including type and card/bank details.
   * @returns The saved payment method.
   *
   * @example
   * ```typescript
   * const pm = await delopay.paymentMethods.create({
   *   payment_method: 'card',
   *   customer_id: 'cus_123',
   *   client_secret: 'cs_...',
   * });
   * ```
   */
  async create(params: PaymentMethodCreateRequest): Promise<PaymentMethodResponse> {
    return this.request('POST', '/payment-methods', { body: params });
  }

  /**
   * Retrieve a saved payment method by its ID.
   *
   * @param methodId - The payment method ID.
   * @returns The payment method.
   */
  async retrieve(methodId: string): Promise<PaymentMethodResponse> {
    return this.request('GET', `/payment-methods/${encodeURIComponent(methodId)}`);
  }

  /**
   * Update an existing payment method (e.g. update card expiry).
   *
   * @param methodId - The payment method ID to update.
   * @param params - Fields to update (card expiry, holder name, etc.).
   * @returns The updated payment method.
   */
  async update(
    methodId: string,
    params: PaymentMethodUpdateRequest,
  ): Promise<PaymentMethodResponse> {
    return this.request('POST', `/payment-methods/${encodeURIComponent(methodId)}/update`, {
      body: params,
    });
  }

  /**
   * Delete a saved payment method.
   *
   * @param methodId - The payment method ID to delete.
   * @returns Deletion confirmation.
   */
  async delete(methodId: string): Promise<PaymentMethodDeleteResponse> {
    return this.request('DELETE', `/payment-methods/${encodeURIComponent(methodId)}`);
  }

  /**
   * List the payment methods available for a payment — the discovery endpoint a
   * custom checkout renders its tiles from.
   *
   * Callable with a publishable key plus the payment's `client_secret`, so it
   * runs from the browser. The returned set is already filtered by country,
   * order value and the merchant's availability rules, and each entry carries
   * `display` (name + icon slug) and `amount_limits` (the order values it stays
   * available for) so you do not have to maintain either alongside.
   *
   * This is *not* the customer's saved methods — see {@link listForCustomer}.
   *
   * @param params - `client_secret`, plus optional `country`, `amount` and filters.
   * @returns The methods available for the payment, grouped by payment method.
   *
   * @example
   * ```typescript
   * const { payment_methods } = await delopay.paymentMethods.list({
   *   client_secret: 'pay_abc_secret_xyz',
   *   country: 'DE',
   *   amount: 25000,
   * });
   *
   * for (const group of payment_methods) {
   *   for (const method of group.payment_method_types) {
   *     // Re-check availability yourself as the cart total changes, instead of
   *     // re-listing on every keystroke.
   *     const limits = method.amount_limits;
   *     const available =
   *       !limits ||
   *       ((limits.min_amount == null || cartTotal >= limits.min_amount) &&
   *         (limits.max_amount == null || cartTotal <= limits.max_amount) &&
   *         !limits.excluded_ranges.some(
   *           (band) => cartTotal >= band.min_amount && cartTotal <= band.max_amount,
   *         ));
   *
   *     if (available) render(method.display?.display_name, method.display?.icon_slug);
   *   }
   * }
   * ```
   */
  async list(params?: PaymentMethodListParams): Promise<PaymentMethodListResponse> {
    return this.request('GET', '/payment-methods', {
      query: params as Record<string, string | number | undefined>,
    });
  }

  /**
   * List all saved payment methods for a customer, optionally filtered.
   *
   * @param customerId - The customer ID.
   * @param params - Optional filters: `client_secret`, `accepted_countries`, `accepted_currencies`,
   *   `amount`, `recurring_enabled`, `installment_payment_enabled`, `limit`, `card_networks`.
   * @returns Customer's saved payment methods.
   *
   * @example
   * ```typescript
   * const { customer_payment_methods } = await delopay.paymentMethods.listForCustomer(
   *   'cus_123',
   *   { accepted_currencies: ['EUR'], amount: 5000 },
   * );
   * ```
   */
  async listForCustomer(
    customerId: string,
    params?: CustomerPaymentMethodsListParams,
  ): Promise<CustomerPaymentMethodsListResponse> {
    return this.request('GET', `/customers/${encodeURIComponent(customerId)}/payment-methods`, {
      query: params as Record<
        string,
        string | number | boolean | (string | number | boolean)[] | null | undefined
      >,
    });
  }

  /**
   * Set a payment method as the default for a customer.
   *
   * @param customerId - The customer ID.
   * @param methodId - The payment method ID to set as default.
   * @returns The updated payment method.
   */
  async setDefault(customerId: string, methodId: string): Promise<PaymentMethodResponse> {
    return this.request(
      'POST',
      `/customers/${encodeURIComponent(customerId)}/payment-methods/${encodeURIComponent(methodId)}/default`,
    );
  }

  // --- Advanced operations (Task 3.3) ---

  /** Migrate a payment method. `POST /payment-methods/migrate` */
  async migrate(params: Record<string, unknown>): Promise<PaymentMethodResponse> {
    return this.request('POST', '/payment-methods/migrate', { body: params });
  }

  /** Batch migrate payment methods. `POST /payment-methods/migrate-batch` */
  async migrateBatch(params: Record<string, unknown>[]): Promise<Record<string, unknown>> {
    return this.request('POST', '/payment-methods/migrate-batch', { body: params });
  }

  /** Batch update payment methods. `POST /payment-methods/update-batch` */
  async updateBatch(params: Record<string, unknown>[]): Promise<Record<string, unknown>> {
    return this.request('POST', '/payment-methods/update-batch', { body: params });
  }

  /** Batch retrieve payment methods. `GET /payment-methods/batch` */
  async batchRetrieve(
    params?: Record<string, string | number | undefined>,
  ): Promise<PaymentMethodResponse[]> {
    return this.request('GET', '/payment-methods/batch', { query: params });
  }

  /** Tokenize a card. `POST /payment-methods/tokenize-card` */
  async tokenizeCard(params: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request('POST', '/payment-methods/tokenize-card', { body: params });
  }

  /** Batch tokenize cards. `POST /payment-methods/tokenize-card-batch` */
  async tokenizeCardBatch(params: Record<string, unknown>[]): Promise<Record<string, unknown>> {
    return this.request('POST', '/payment-methods/tokenize-card-batch', { body: params });
  }

  /** Initiate payment method collect link flow. `POST /payment-methods/collect` */
  async collect(params: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request('POST', '/payment-methods/collect', { body: params });
  }

  /** Save a payment method. `POST /payment-methods/{methodId}/save` */
  async save(methodId: string, params?: Record<string, unknown>): Promise<PaymentMethodResponse> {
    return this.request('POST', `/payment-methods/${encodeURIComponent(methodId)}/save`, {
      body: params,
    });
  }

  /** Create payment method auth link token. `POST /payment-methods/auth/link` */
  async createAuthLink(params: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request('POST', '/payment-methods/auth/link', { body: params });
  }

  /** Exchange payment method auth token. `POST /payment-methods/auth/exchange` */
  async exchangeAuthToken(params: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request('POST', '/payment-methods/auth/exchange', { body: params });
  }

  /** Tokenize card using existing PM. `POST /payment-methods/{methodId}/tokenize-card` */
  async tokenizeCardForMethod(
    methodId: string,
    params: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request('POST', `/payment-methods/${encodeURIComponent(methodId)}/tokenize-card`, {
      body: params,
    });
  }
}
